/**
 * BLUORNG-STYLE WISHLIST FEATURE
 * Compatible with Shopify Stiletto Theme
 * Handles: guest (localStorage) + logged-in (metafields) wishlists
 * v2: Checkboxes, live stock check, selective checkout
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.init();
  }

  getLocalWishlist() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey)) || [];
    } catch { return []; }
  }

  saveLocalWishlist(items) {
    localStorage.setItem(this.storageKey, JSON.stringify(items));
  }

  async getServerWishlist() {
    try {
      const res = await fetch('/apps/wishlist/get', {
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data.items || [];
    } catch { return null; }
  }

  async saveServerWishlist(items) {
    try {
      await fetch('/apps/wishlist/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
    } catch { /* silent fail */ }
  }

  isLoggedIn() {
    return window.__wishlistCustomerLoggedIn === true;
  }

  async getWishlist() {
    if (this.isLoggedIn()) {
      const serverList = await this.getServerWishlist();
      if (serverList !== null) return serverList;
    }
    return this.getLocalWishlist();
  }

  async addItem(productId, variantId, productTitle, productImage, price, size, handle) {
    const items = await this.getWishlist();
    const exists = items.find(i => i.variantId === String(variantId));
    if (exists) return false;

    const newItem = {
      productId: String(productId),
      variantId: String(variantId),
      title: productTitle,
      image: productImage,
      price: price,
      size: size,
      handle: handle || String(productId),
      addedAt: Date.now()
    };

    items.push(newItem);
    this.saveLocalWishlist(items);
    if (this.isLoggedIn()) await this.saveServerWishlist(items);

    this.updateCountBadge(items.length);
    this.updateAllButtons();
    return true;
  }

  async removeItem(variantId) {
    let items = await this.getWishlist();
    items = items.filter(i => i.variantId !== String(variantId));
    this.saveLocalWishlist(items);
    if (this.isLoggedIn()) await this.saveServerWishlist(items);
    this.updateCountBadge(items.length);
    this.updateAllButtons();
    return items;
  }

  async isInWishlist(variantId) {
    const items = await this.getWishlist();
    return items.some(i => i.variantId === String(variantId));
  }

  async getCount() {
    const items = await this.getWishlist();
    return items.length;
  }

  updateCountBadge(count) {
    document.querySelectorAll('.wishlist-count-badge').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  updateAllButtons() {
    const localItems = this.getLocalWishlist();

    document.querySelectorAll('[data-wishlist-card-btn]').forEach(btn => {
      const variantIds = JSON.parse(btn.dataset.variants || '[]').map(v => String(v.id));
      const inList = localItems.some(i => variantIds.includes(String(i.variantId)));
      btn.classList.toggle('in-wishlist', inList);
    });

    const pageBtn = document.querySelector('[data-add-to-wishlist-page]');
    if (pageBtn) {
      const productId = pageBtn.dataset.productId;
      const existingItem = localItems.find(i => String(i.productId) === String(productId));
      const textEl = pageBtn.querySelector('.wishlist-page-btn-text');
      const svgPath = pageBtn.querySelector('svg path');
      if (existingItem) {
        pageBtn.classList.add('in-wishlist');
        if (textEl) textEl.textContent = 'Saved to Wishlist';
        if (svgPath) svgPath.setAttribute('fill', 'currentColor');
      } else {
        pageBtn.classList.remove('in-wishlist');
        if (textEl) textEl.textContent = 'Add to Wishlist';
        if (svgPath) svgPath.setAttribute('fill', 'none');
      }
    }
  }

  async init() {
    const count = await this.getCount();
    this.updateCountBadge(count);
    this.updateAllButtons();
  }
}

// ─── Size Picker Modal ─────────────────────────────────────────────────────────

class WishlistSizePicker {
  constructor() {
    this.createModal();
  }

  createModal() {
    const modal = document.createElement('div');
    modal.id = 'wishlist-size-modal';
    modal.className = 'wishlist-modal-overlay';
    modal.innerHTML = `
      <div class="wishlist-modal-box">
        <button class="wishlist-modal-close" aria-label="Close">&times;</button>
        <div class="wishlist-modal-product">
          <div class="wishlist-modal-title"></div>
          <div class="wishlist-modal-price"></div>
        </div>
        <div class="wishlist-modal-label">Size</div>
        <div class="wishlist-modal-sizes"></div>
        <button class="wishlist-modal-cta">Add To List</button>
      </div>
    `;
    document.body.appendChild(modal);

    modal.querySelector('.wishlist-modal-close').addEventListener('click', () => this.close());
    modal.addEventListener('click', e => { if (e.target === modal) this.close(); });
  }

  open({ productId, title, price, image, variants }) {
    return new Promise((resolve) => {
      const modal = document.getElementById('wishlist-size-modal');
      modal.querySelector('.wishlist-modal-title').textContent = title;
      modal.querySelector('.wishlist-modal-price').textContent = price;

      const sizesContainer = modal.querySelector('.wishlist-modal-sizes');
      sizesContainer.innerHTML = '';

      let selectedVariant = null;

      variants.forEach(variant => {
        const btn = document.createElement('button');
        btn.className = 'wishlist-size-btn';
        btn.textContent = variant.title;
        btn.dataset.variantId = variant.id;

        if (!variant.available) {
          btn.classList.add('sold-out');
          btn.disabled = true;
        }

        btn.addEventListener('click', () => {
          sizesContainer.querySelectorAll('.wishlist-size-btn').forEach(b => b.classList.remove('selected'));
          btn.classList.add('selected');
          selectedVariant = variant;
        });

        sizesContainer.appendChild(btn);
      });

      if (variants.length === 1 && variants[0].available) {
        sizesContainer.querySelector('.wishlist-size-btn').classList.add('selected');
        selectedVariant = variants[0];
      }

      const cta = modal.querySelector('.wishlist-modal-cta');
      cta.onclick = () => {
        if (!selectedVariant) {
          sizesContainer.classList.add('shake');
          setTimeout(() => sizesContainer.classList.remove('shake'), 400);
          return;
        }
        this.close();
        resolve({ variantId: selectedVariant.id, size: selectedVariant.title, productId, title, image, price });
      };

      modal.classList.add('open');
      requestAnimationFrame(() => modal.querySelector('.wishlist-modal-box').classList.add('visible'));
    });
  }

  close() {
    const modal = document.getElementById('wishlist-size-modal');
    modal.querySelector('.wishlist-modal-box').classList.remove('visible');
    setTimeout(() => modal.classList.remove('open'), 250);
  }
}

// ─── Toast Notifications ───────────────────────────────────────────────────────

class WishlistToast {
  show({ title, image, removed = false, viewUrl = '/pages/wishlist' }) {
    let toast = document.getElementById('wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'wishlist-toast';
      document.body.appendChild(toast);
    }

    const safeImage = image
      ? (image.startsWith('//') ? 'https:' + image : image)
      : '';

    clearTimeout(this._timer1);
    clearTimeout(this._timer2);
    clearTimeout(this._timer3);


    toast.innerHTML = `
      <div class="wishlist-toast-inner saved">
        <img src="${safeImage}" alt="${title}" class="wishlist-toast-img" />
        <span class="wishlist-toast-text">${removed ? 'Item Removed' : 'Item Saved'}</span>
      </div>
    `;

    toast.style.opacity = '0';
    toast.style.transform = 'translateY(16px)';
    toast.style.pointerEvents = 'auto';
    requestAnimationFrame(() => {
      toast.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      toast.style.opacity = '1';
      toast.style.transform = 'translateY(0)';
    });

    this._timer1 = setTimeout(() => {
      toast.innerHTML = `
        <div class="wishlist-toast-inner expanded">
          <img src="${safeImage}" alt="${title}" class="wishlist-toast-img" />
          <div class="wishlist-toast-content">
            <span class="wishlist-toast-title">${title}</span>
            <div class="wishlist-toast-buttons">
              <a href="${viewUrl}" class="wishlist-toast-btn wishlist-toast-btn--outline">View Wishlist</a>
            </div>
          </div>
        </div>
      `;
    }, 3000);

    this._timer2 = setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(16px)';
      toast.style.pointerEvents = 'none';
    }, 8000);

    this._timer3 = setTimeout(() => {
      toast.innerHTML = '';
    }, 8400);
  }
}

// ─── Wishlist Page ─────────────────────────────────────────────────────────────

class WishlistPage {
  constructor() {
    this.stockData = {}; // cache: variantId -> available (bool)
    this.checkedItems = new Set(); // variantIds that are checked
  }

  async render(containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;

    // Show loading state
    container.innerHTML = `<div class="wishlist-loading"><p>Loading your wishlist...</p></div>`;

    const items = await window.Wishlist.getWishlist();

    if (items.length === 0) {
      this.renderEmpty(container);
      return;
    }

    // Fetch live stock for all items
    await this.fetchStockData(items);

    // Pre-check all in-stock items
    this.checkedItems = new Set(
      items
        .filter(item => this.stockData[item.variantId] !== false)
        .map(item => item.variantId)
    );

    this.renderItems(container, items);
  }

  async fetchStockData(items) {
    // Fetch each product's live availability from Shopify
    const handles = [...new Set(items.map(i => i.handle).filter(Boolean))];

    await Promise.all(handles.map(async handle => {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return;
        const product = await res.json();

        product.variants.forEach(variant => {
          this.stockData[String(variant.id)] = variant.available;
        });
      } catch {
        // If fetch fails, assume in stock
      }
    }));
  }

  renderEmpty(container) {
    container.innerHTML = `
      <div class="wishlist-empty">
        <svg width="40" height="48" viewBox="0 0 18 22" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M1 1h16v20l-8-5-8 5V1z"/></svg>
        <p>Your wishlist is empty</p>
        <a href="/collections/all" class="btn btn--primary btn--full">Start Shopping</a>
      </div>
    `;
  }

  renderItems(container, items) {
    const inStockItems = items.filter(i => this.stockData[i.variantId] !== false);
    const inStockCount = inStockItems.length;
    const checkedItems = items.filter(i => this.checkedItems.has(i.variantId));
    const selectedCount = checkedItems.length;
    const totalValue = checkedItems.reduce((sum, item) => {
      const numericPrice = parseFloat(String(item.price).replace(/[^0-9]/g, '')) / 100 || 0;
      return sum + numericPrice;
    }, 0);


    container.innerHTML = `
      <div class="wishlist-grid">
        ${items.map(item => this.renderCard(item)).join('')}
      </div>
      <div style="height: 120px;"></div>
      <div class="wishlist-footer-bar">
        <div class="wishlist-footer-stats">
          <span class="wishlist-stat-pill">Wishlist</span>
          <span class="wishlist-stat-pill">Product count : ${items.length}</span>
          <span class="wishlist-stat-pill">In stock : ${inStockCount}</span>
        </div>
        <div class="wishlist-footer-actions">
          <span class="wishlist-footer-count">Selected count : <span id="wishlist-selected-count">${selectedCount}</span></span>
          <span class="wishlist-footer-total">Total Value : Rs. <span id="wishlist-total-value">${totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
          <button class="btn btn--primary" id="wishlist-checkout-all">Proceed to Checkout</button>
        </div>
      </div>
    `;

    // Bind checkbox events
    container.querySelectorAll('.wishlist-item-checkbox').forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const variantId = checkbox.dataset.variantId;
        if (checkbox.checked) {
          this.checkedItems.add(variantId);
        } else {
          this.checkedItems.delete(variantId);
        }
        this.updateFooter(items);
      });
    });

    // Bind remove buttons
    container.querySelectorAll('[data-remove-wishlist]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = btn.dataset.removeWishlist;
        this.checkedItems.delete(variantId);
        await window.Wishlist.removeItem(variantId);
        const updated = await window.Wishlist.getWishlist();
        if (updated.length === 0) {
          this.renderEmpty(container);
        } else {
          this.renderItems(container, updated);
        }
      });
    });

    // Bind checkout button
    document.getElementById('wishlist-checkout-all')?.addEventListener('click', async () => {
      const selectedItems = items.filter(i => this.checkedItems.has(i.variantId));
      if (selectedItems.length === 0) {
        alert('Please select at least one item to checkout.');
        return;
      }
      await this.addSelectedToCart(selectedItems);
    });
  }

  renderCard(item) {
    const imageUrl = item.image
      ? (item.image.startsWith('//') ? 'https:' + item.image : item.image)
      : '';

    const isAvailable = this.stockData[item.variantId] !== false;
    const isChecked = this.checkedItems.has(item.variantId);

   const outOfStockLabel = !isAvailable ? `
  <div class="product-badges product-badges--position-top product-badges--position-left">
    <div class="product-badge" data-handle="selected-size-sold-out">Selected Size: Sold Out</div>
  </div>
` : '';

    const outOfStockOverlay = !isAvailable ? `
      <div class="wishlist-out-of-stock-overlay"></div>
    ` : '';

    return `
      <div class="wishlist-card ${!isAvailable ? 'wishlist-card--out-of-stock' : ''}" data-variant="${item.variantId}">
        <a href="/products/${item.handle || item.productId}" class="wishlist-card-img-wrap">
          <img src="${imageUrl}" alt="${item.title}" class="wishlist-card-img" loading="lazy" />
          ${outOfStockOverlay}
          ${outOfStockLabel}
          <button class="wishlist-card-bookmark" data-remove-wishlist="${item.variantId}" aria-label="Remove from wishlist">
            <svg width="15" height="19" viewBox="0 0 18 22" fill="currentColor" stroke="currentColor" stroke-width="1.6"><path d="M1 1h16v20l-8-5-8 5V1z"/></svg>
          </button>
        </a>
        <div class="wishlist-card-info">
          <div class="wishlist-card-header">
            <span class="wishlist-card-title">${item.title}</span>
            <label class="wishlist-checkbox-wrap" title="${!isAvailable ? 'Out of stock' : 'Select for checkout'}">
              <input
                type="checkbox"
                class="wishlist-item-checkbox"
                data-variant-id="${item.variantId}"
                ${isChecked ? 'checked' : ''}
                ${!isAvailable ? 'disabled' : ''}
              />
              <span class="wishlist-checkbox-custom"></span>
            </label>
          </div>
          <span class="wishlist-card-size">Selected Size: <strong>${item.size}</strong></span>
          <span class="wishlist-card-price">${item.price}</span>
        </div>
      </div>
    `;
  }

  updateFooter(items) {
    const checkedItems = items.filter(i => this.checkedItems.has(i.variantId));
    const selectedCount = checkedItems.length;
    const totalValue = checkedItems.reduce((sum, item) => {
      const numericPrice = parseFloat(String(item.price).replace(/[^0-9]/g, '')) / 100 || 0;
      return sum + numericPrice;
    }, 0);

    const countEl = document.getElementById('wishlist-selected-count');
    const totalEl = document.getElementById('wishlist-total-value');
    if (countEl) countEl.textContent = selectedCount;
    if (totalEl) totalEl.textContent = totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  async addSelectedToCart(items) {
    const btn = document.getElementById('wishlist-checkout-all');
    if (btn) { btn.textContent = 'Adding...'; btn.disabled = true; }

    try {
      const items_payload = items.map(item => ({
        id: parseInt(item.variantId),
        quantity: 1
      }));

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items_payload })
      });

      // Reset button then redirect to cart for Razorpay to reinitialize
      if (btn) { btn.textContent = 'Proceed to Checkout'; btn.disabled = false; }
      sessionStorage.setItem('wishlist_trigger_checkout', '1');
      window.location.href = '/cart';

    } catch (e) {
      if (btn) { btn.textContent = 'Proceed to Checkout'; btn.disabled = false; }
      console.error('Wishlist checkout error:', e);
    }
  }
}

// ─── Product Page "Add to Wishlist" ───────────────────────────────────────────

async function initProductPageWishlist() {
  const btn = document.querySelector('[data-add-to-wishlist-page]');
  if (!btn) return;

  const productId = btn.dataset.productId;
  const productTitle = btn.dataset.productTitle;
  const productImage = btn.dataset.productImage;
  const productPrice = btn.dataset.productPrice;
  const handle = btn.dataset.productHandle;

  function getSelectedVariantId() {
    const form = document.querySelector('form[action="/cart/add"]');
    const input = form?.querySelector('[name="id"]');
    return input?.value || null;
  }

  function checkIfWishlisted() {
    const localItems = window.Wishlist.getLocalWishlist();
    return localItems.find(i => String(i.productId) === String(productId));
  }

  function setSavedState() {
    btn.classList.add('in-wishlist');
    btn.querySelector('.wishlist-page-btn-text').textContent = 'Saved to Wishlist';
    const svg = btn.querySelector('svg path');
    if (svg) svg.setAttribute('fill', 'currentColor');
  }

  function setUnsavedState() {
    btn.classList.remove('in-wishlist');
    btn.querySelector('.wishlist-page-btn-text').textContent = 'Add to Wishlist';
    const svg = btn.querySelector('svg path');
    if (svg) svg.setAttribute('fill', 'none');
  }

  const alreadySaved = checkIfWishlisted();
  if (alreadySaved) {
    setSavedState();
  } else {
    setUnsavedState();
  }

  btn.addEventListener('click', async () => {
    const existingItem = checkIfWishlisted();

    if (existingItem) {
      await window.Wishlist.removeItem(existingItem.variantId);
      setUnsavedState();
      window.Wishlist.updateAllButtons();
      window.WishlistToast.show({
        title: existingItem.title,
        image: existingItem.image,
        removed: true
      });
      return;
    }

    const selectedVariantId = getSelectedVariantId();
    if (!selectedVariantId) {
      document.querySelector('.product-form__input')?.classList.add('wishlist-highlight');
      return;
    }

    const selectedSizeLabel = document.querySelector(
      '.variant-picker [aria-pressed="true"], input[name="Size"]:checked + label'
    )?.textContent?.trim()
      || document.querySelector('select[name="Size"]')?.value
      || 'One Size';

    const added = await window.Wishlist.addItem(
      productId, selectedVariantId, productTitle, productImage, productPrice, selectedSizeLabel, handle
    );

    if (added) {
      setSavedState();
      window.Wishlist.updateAllButtons();
      window.WishlistToast.show({ title: productTitle, image: productImage });
    }
  });
}

// ─── Card Buttons (Collection / Home Page Grids) ─────────────────────────────

async function initCardWishlistButtons() {
  document.querySelectorAll('[data-wishlist-card-btn]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const productId = btn.dataset.productId;
      const productTitle = btn.dataset.productTitle;
      const productImage = btn.dataset.productImage;
      const productPrice = btn.dataset.productPrice;
      const variantsJson = btn.dataset.variants;
      const handle = btn.dataset.productHandle;

      let variants = [];
      try { variants = JSON.parse(variantsJson); } catch {}

      const localItems = window.Wishlist.getLocalWishlist();
      const existingItem = localItems.find(i =>
        variants.some(v => String(v.id) === String(i.variantId))
      );

      if (existingItem) {
        await window.Wishlist.removeItem(existingItem.variantId);
        btn.classList.remove('in-wishlist');
        window.WishlistToast.show({
          title: productTitle,
          image: productImage,
          removed: true
        });
        return;
      }

      if (variants.length === 1 && (variants[0].title === 'Default Title' || variants[0].title === '')) {
        const v = variants[0];
        await window.Wishlist.addItem(productId, v.id, productTitle, productImage, productPrice, 'One Size', handle);
        btn.classList.add('in-wishlist');
        window.WishlistToast.show({ title: productTitle, image: productImage });
        return;
      }

      const result = await window.WishlistSizePicker.open({
        productId, title: productTitle, price: productPrice, image: productImage, variants
      });

      if (result) {
        await window.Wishlist.addItem(
          result.productId, result.variantId, result.title, result.image, result.price, result.size, handle
        );
        btn.classList.add('in-wishlist');
        window.WishlistToast.show({ title: result.title, image: result.image });
      }
    });
  });
}

// ─── Sync guest wishlist to account after login ───────────────────────────────

async function syncGuestWishlistOnLogin() {
  if (!window.__wishlistCustomerLoggedIn) return;
  const localItems = JSON.parse(localStorage.getItem('shopify_wishlist') || '[]');
  if (localItems.length === 0) return;

  try {
    const serverItems = await window.Wishlist.getServerWishlist() || [];
    const merged = [...serverItems];
    localItems.forEach(local => {
      if (!merged.find(s => s.variantId === local.variantId)) merged.push(local);
    });
    await window.Wishlist.saveServerWishlist(merged);
    window.Wishlist.saveLocalWishlist(merged);
    localStorage.removeItem('shopify_wishlist_synced');
  } catch {}
}

// ─── Bootstrap ────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', async () => {
  window.Wishlist = new WishlistManager();
  window.WishlistSizePicker = new WishlistSizePicker();
  window.WishlistToast = new WishlistToast();

  initProductPageWishlist();
  await initCardWishlistButtons();

  if (document.getElementById('wishlist-page-container')) {
    const page = new WishlistPage();
    await page.render('wishlist-page-container');

    // Hide wishlist footer bar when site footer is visible
    setTimeout(() => {
      const siteFooter = document.querySelector('footer, .footer__outer-wrapper, [data-section-type="footer"], .footer-section');
      const wishlistBar = document.querySelector('.wishlist-footer-bar');

      if (siteFooter && wishlistBar) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              wishlistBar.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
              wishlistBar.style.transform = 'translateY(100%)';
              wishlistBar.style.opacity = '0';
              wishlistBar.style.pointerEvents = 'none';
            } else {
              wishlistBar.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
              wishlistBar.style.transform = 'translateY(0)';
              wishlistBar.style.opacity = '1';
              wishlistBar.style.pointerEvents = 'auto';
            }
          });
        }, { threshold: 0.05 });

        observer.observe(siteFooter);
      }
    }, 1000);
  }
});